import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useCashflows, useProfile, useResidencyCountries } from '../../hooks/useApi';
import { useExchangeRates } from '../../hooks/useExchangeRates';
import { formatCurrencyInput, extractNumericInput, formatAmount } from '../../utils/numberFormat';
import {
    CashFlowType,
    CashFlowCategory,
    DynamicCashFlow,
    Country,
    Currency,
    CASHFLOW_CATEGORY_LABELS,
    INCOME_CATEGORIES,
    EXPENSE_CATEGORIES,
    COUNTRY_CURRENCIES,
} from '../../types/api.types';
import styles from './CashflowPage.module.scss';

type ActiveTab = 'INCOME' | 'EXPENSE' | 'RETIREMENT';

const COUNTRIES: { value: Country; label: string }[] = [
    { value: 'US', label: 'United States' },
    { value: 'INDIA', label: 'India' },
    { value: 'CAN', label: 'Canada' },
];

const DEFAULT_LIFE_EXPECTANCY: Record<Country, number> = { INDIA: 80, US: 83, CAN: 84 };

function getFireMultiplier(retirementYears: number): number {
    if (retirementYears <= 30) return 25;
    if (retirementYears <= 35) return 28;
    if (retirementYears <= 40) return 30;
    if (retirementYears <= 45) return 32;
    return 33;
}

function inflationAdjust(amount: number, growthRate: number, years: number): number {
    return amount * Math.pow(1 + growthRate / 100, years);
}

function convertCurrency(
    amount: number,
    fromCurrency: Currency,
    toCurrency: Currency,
    rates: Record<Currency, number>,
): number {
    if (fromCurrency === toCurrency) return amount;
    return (amount / rates[fromCurrency]) * rates[toCurrency];
}

const VALID_TABS: ActiveTab[] = ['INCOME', 'EXPENSE', 'RETIREMENT'];

export function CashflowPage() {
    const [searchParams, setSearchParams] = useSearchParams();
    const tabParam = searchParams.get('tab')?.toUpperCase() as ActiveTab | null;
    const initialTab: ActiveTab = tabParam && VALID_TABS.includes(tabParam) ? tabParam : 'INCOME';
    const [activeTab, setActiveTab] = useState<ActiveTab>(initialTab);

    const handleTabChange = (tab: ActiveTab) => {
        setActiveTab(tab);
        setSearchParams({ tab }, { replace: true });
    };
    const [showForm, setShowForm] = useState(false);
    const [selectedCashflow, setSelectedCashflow] = useState<DynamicCashFlow | null>(null);
    const { cashflows, loading, error, refetch, addCashflow, updateCashflow, deleteCashflow } = useCashflows();
    const { profile } = useProfile();
    const { rates, loading: ratesLoading } = useExchangeRates();
    const baseCurrency = profile?.baseCurrency ?? 'USD';
    const currentAge = profile?.currentAge ?? 35;
    const profileRetirementAge = profile?.targetRetirementAge ?? 60;
    const yearsToRetirement = Math.max(0, profileRetirementAge - currentAge);

    const retirementCountry: Country = (() => {
        const history = profile?.residencyHistory ?? [];
        const atRetirement = history.find(
            (r) => profileRetirementAge >= r.startAge && profileRetirementAge <= r.endAge,
        );
        if (atRetirement) return atRetirement.country;
        const sorted = [...history].sort((a, b) => b.endAge - a.endAge);
        return sorted[0]?.country ?? 'US';
    })();
    const lifeExpectancy = DEFAULT_LIFE_EXPECTANCY[retirementCountry] ?? 83;
    const retirementYears = Math.max(0, lifeExpectancy - profileRetirementAge);
    const fireMultiplier = getFireMultiplier(retirementYears);

    let filtered: DynamicCashFlow[] = [];
    let total = 0;
    let oneTimeTotal = 0;
    let activeAtCurrentAge: DynamicCashFlow[] = [];
    let displayLabel = '';

    if (activeTab === 'RETIREMENT') {
        filtered = cashflows.filter((cf) => cf.type === 'EXPENSE' && cf.isRetirementExpense);
        const recurringExpenses = filtered.filter((cf) => !cf.isOneTime);
        const oneTimeExpenses = filtered.filter((cf) => cf.isOneTime);
        total = recurringExpenses.reduce(
            (sum, cf) => sum + convertCurrency(cf.amount, cf.currency, baseCurrency, rates),
            0,
        );
        oneTimeTotal = oneTimeExpenses.reduce(
            (sum, cf) => sum + convertCurrency(cf.amount, cf.currency, baseCurrency, rates),
            0,
        );
        displayLabel = `MONTHLY RETIREMENT BUDGET (at age ${profileRetirementAge})`;
    } else {
        filtered = cashflows.filter(
            (cf) => cf.type === activeTab && (activeTab !== 'EXPENSE' || !cf.isRetirementExpense),
        );
        activeAtCurrentAge = currentAge
            ? filtered.filter((cf) => currentAge >= cf.startAge && currentAge <= cf.endAge)
            : filtered;
        total = activeAtCurrentAge.reduce(
            (sum, cf) => sum + convertCurrency(cf.amount, cf.currency, baseCurrency, rates),
            0,
        );
        displayLabel = `MONTHLY ${activeTab} ${currentAge ? `AT AGE ${currentAge}` : '(ALL)'}`;
    }

    if (loading || ratesLoading) {
        return (
            <div className={styles.page}>
                <p className={styles.loading}>Loading cashflows...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className={styles.page}>
                <p className={styles.error}>{error}</p>
                <button className={styles.retryButton} onClick={refetch}>
                    Retry
                </button>
            </div>
        );
    }

    const fireNumber = activeTab === 'RETIREMENT' ? total * 12 * fireMultiplier + oneTimeTotal : 0;

    return (
        <div className={styles.page}>
            <div className={styles.pageHeader}>
                <div>
                    <h1 className={styles.title}>Income & Expenses</h1>
                    <p className={styles.subtitle}>Manage your recurring and one-time cash flows.</p>
                </div>
                <div className={styles.headerActions}>
                    <div className={styles.tabs}>
                        <button
                            className={`${styles.tab} ${activeTab === 'INCOME' ? styles.active : ''}`}
                            onClick={() => handleTabChange('INCOME')}
                        >
                            Income
                        </button>
                        <button
                            className={`${styles.tab} ${activeTab === 'EXPENSE' ? styles.active : ''}`}
                            onClick={() => handleTabChange('EXPENSE')}
                        >
                            Expenses
                        </button>
                        <button
                            className={`${styles.tab} ${activeTab === 'RETIREMENT' ? styles.active : ''}`}
                            onClick={() => handleTabChange('RETIREMENT')}
                        >
                            Retirement
                        </button>
                    </div>
                    <button className={styles.addButton} onClick={() => setShowForm(true)}>
                        + Add {activeTab === 'INCOME' ? 'Income' : activeTab === 'EXPENSE' ? 'Expense' : 'Retirement'}
                    </button>
                </div>
            </div>

            {activeTab === 'RETIREMENT' && (
                <div className={styles.tabHint}>
                    <p className={styles.tabHintTitle}>Two types of retirement cashflows</p>
                    <ul className={styles.tabHintList}>
                        <li>
                            <strong>Recurring:</strong> monthly budget you expect every year in retirement (e.g.,
                            ₹2L/month living expenses). Entered as a monthly amount and grows with inflation.
                        </li>
                        <li>
                            <strong>One-time:</strong> a single lump-sum spend during retirement (e.g., ₹50L home
                            renovation). No fixed age — it is deducted at whatever retirement age you test on the
                            simulator slider, so you can compare retiring at 55 vs 60 vs 65 without re-entering data.
                        </li>
                        <li>
                            <strong>Already added this in Goals?</strong> Don't add it here too — the simulator deducts
                            both separately from your portfolio.
                        </li>
                    </ul>
                </div>
            )}

            {activeTab === 'RETIREMENT' && (
                <>
                    {filtered.length > 0 && (
                        <div className={styles.retirementSummary}>
                            <div className={styles.summaryRow}>
                                <div className={styles.summaryItem}>
                                    <span className={styles.summaryLabel}>MONTHLY RECURRING</span>
                                    <span className={styles.summaryValue}>
                                        {formatAmount(Math.round(total), baseCurrency)}
                                    </span>
                                </div>
                                <div className={styles.summaryItem}>
                                    <span className={styles.summaryLabel}>ANNUAL RECURRING</span>
                                    <span className={styles.summaryValue}>
                                        {formatAmount(Math.round(total * 12), baseCurrency)}
                                    </span>
                                </div>
                                {oneTimeTotal > 0 && (
                                    <div className={styles.summaryItem}>
                                        <span className={styles.summaryLabel}>ONE-TIME EVENTS</span>
                                        <span className={styles.summaryValue}>
                                            {formatAmount(Math.round(oneTimeTotal), baseCurrency)}
                                        </span>
                                    </div>
                                )}
                            </div>
                            <div className={styles.summaryRow}>
                                <div className={styles.summaryItem}>
                                    <span className={styles.summaryLabel}>FIRE NUMBER ({fireMultiplier}×)</span>
                                    <span className={styles.summaryValue}>
                                        {formatAmount(Math.round(fireNumber), baseCurrency)}
                                    </span>
                                    <span className={styles.fireNumberNote}>
                                        {formatAmount(Math.round(total * 12), baseCurrency)}/yr × {fireMultiplier}
                                        {oneTimeTotal > 0
                                            ? ` + ${formatAmount(Math.round(oneTimeTotal), baseCurrency)} one-time`
                                            : ''}{' '}
                                        ({retirementYears}-yr horizon)
                                    </span>
                                </div>
                            </div>
                        </div>
                    )}
                </>
            )}

            <div className={styles.totalCard}>
                <span className={styles.totalLabel}>{displayLabel}</span>
                <span className={styles.totalValue}>{formatAmount(Math.round(total), baseCurrency)}</span>
                <span className={styles.count}>
                    {activeTab === 'RETIREMENT'
                        ? filtered.filter((cf) => !cf.isOneTime).length
                        : activeAtCurrentAge.length}{' '}
                    of {filtered.length} item(s)
                </span>
            </div>

            {filtered.length === 0 ? (
                <div className={styles.emptyState}>
                    <p>
                        No{' '}
                        {activeTab === 'INCOME' ? 'income' : activeTab === 'EXPENSE' ? 'expense' : 'retirement budget'}{' '}
                        items yet.
                    </p>
                </div>
            ) : (
                <div className={styles.cashflowList}>
                    {filtered.map((cf) => (
                        <CashflowCard
                            key={cf.cashFlowId}
                            cashflow={cf}
                            onEdit={setSelectedCashflow}
                            onDelete={deleteCashflow}
                            yearsToEvent={activeTab === 'RETIREMENT' ? yearsToRetirement : 0}
                        />
                    ))}
                </div>
            )}

            {showForm && (
                <AddCashflowModal
                    type={activeTab === 'RETIREMENT' ? 'EXPENSE' : activeTab}
                    onClose={() => setShowForm(false)}
                    onAdded={() => {
                        setShowForm(false);
                        refetch();
                    }}
                    addCashflow={addCashflow}
                    targetRetirementAge={activeTab === 'RETIREMENT' ? profileRetirementAge : undefined}
                />
            )}

            {selectedCashflow && (
                <EditCashflowModal
                    cashflow={selectedCashflow}
                    onClose={() => setSelectedCashflow(null)}
                    onSaved={() => {
                        setSelectedCashflow(null);
                        refetch();
                    }}
                    updateCashflow={updateCashflow}
                />
            )}
        </div>
    );
}

function CashflowCard({
    cashflow,
    onEdit,
    onDelete,
    yearsToEvent,
}: {
    cashflow: DynamicCashFlow;
    onEdit: (cashflow: DynamicCashFlow) => void;
    onDelete: (id: string) => void;
    yearsToEvent: number;
}) {
    const futureAmount =
        yearsToEvent > 0 ? inflationAdjust(cashflow.amount, cashflow.growthRate, yearsToEvent) : cashflow.amount;

    return (
        <div className={styles.card}>
            <div className={styles.cardHeader}>
                <span className={styles.cardLabel}>
                    {cashflow.label || CASHFLOW_CATEGORY_LABELS[cashflow.category]}
                </span>
                <div className={styles.cardMeta}>
                    {cashflow.isOneTime && <span className={styles.oneTimeBadge}>One-time</span>}
                    <span className={styles.cardCountry}>{cashflow.country}</span>
                </div>
            </div>
            <div className={styles.cardBody}>
                <span className={styles.cardAmount}>{formatAmount(cashflow.amount, cashflow.currency)}</span>
                <span className={styles.cardMeta}>
                    {cashflow.isOneTime ? (
                        cashflow.isRetirementExpense ? (
                            <>At retirement</>
                        ) : (
                            <>Age {cashflow.startAge}</>
                        )
                    ) : cashflow.isRetirementExpense ? (
                        <>
                            Retirement age–{cashflow.endAge}
                            {cashflow.growthRate > 0 && ` · ${cashflow.growthRate}% growth`}
                        </>
                    ) : cashflow.endsAtRetirement ? (
                        <>
                            Age {cashflow.startAge}–retirement
                            {cashflow.growthRate > 0 && ` · ${cashflow.growthRate}% growth`}
                        </>
                    ) : (
                        <>
                            Age {cashflow.startAge}–{cashflow.endAge}
                            {cashflow.growthRate > 0 && ` · ${cashflow.growthRate}% growth`}
                        </>
                    )}
                </span>
            </div>
            {yearsToEvent > 0 &&
                futureAmount !== cashflow.amount &&
                !cashflow.isOneTime &&
                !cashflow.isRetirementExpense && (
                    <div className={styles.inflationNote}>
                        {formatAmount(cashflow.amount, cashflow.currency)} today →{' '}
                        {formatAmount(Math.round(futureAmount), cashflow.currency)} at retirement
                    </div>
                )}
            <div className={styles.cardActions}>
                <button className={styles.editButton} onClick={() => onEdit(cashflow)}>
                    Edit
                </button>
                <button className={styles.deleteButton} onClick={() => onDelete(cashflow.cashFlowId)}>
                    Remove
                </button>
            </div>
        </div>
    );
}

interface EditCashflowModalProps {
    cashflow: DynamicCashFlow;
    onClose: () => void;
    onSaved: () => void;
    updateCashflow: (
        cashFlowId: string,
        patch: Partial<Omit<DynamicCashFlow, 'cashFlowId' | 'memberSub' | 'updatedAt'>>,
    ) => Promise<DynamicCashFlow>;
}

function EditCashflowModal({ cashflow, onClose, onSaved, updateCashflow }: EditCashflowModalProps) {
    const { rates } = useExchangeRates();
    const { profile } = useProfile();
    const baseCurrency = profile?.baseCurrency ?? 'USD';
    const categories = cashflow.type === 'INCOME' ? INCOME_CATEGORIES : EXPENSE_CATEGORIES;
    const [category, setCategory] = useState<CashFlowCategory>(cashflow.category);
    const [label, setLabel] = useState(cashflow.label ?? '');
    const [country, setCountry] = useState<Country>(cashflow.country);
    const currency = COUNTRY_CURRENCIES[country];
    const [amount, setAmount] = useState(String(cashflow.amount));
    const [growthRate, setGrowthRate] = useState(String(cashflow.growthRate));
    const [startAge, setStartAge] = useState(String(cashflow.startAge));
    const [endAge, setEndAge] = useState(String(cashflow.endAge));
    const [isOneTime] = useState(!!cashflow.isOneTime);
    const [endsAtRetirement, setEndsAtRetirement] = useState(!!cashflow.endsAtRetirement);
    const [saving, setSaving] = useState(false);
    const [formError, setFormError] = useState<string | null>(null);

    const numericAmount = Number(extractNumericInput(amount));
    const convertedAmount =
        numericAmount > 0 && currency !== baseCurrency
            ? convertCurrency(numericAmount, currency, baseCurrency, rates)
            : numericAmount;

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setSaving(true);
        setFormError(null);
        try {
            await updateCashflow(cashflow.cashFlowId, {
                category,
                label: label || undefined,
                country,
                amount: Number(extractNumericInput(amount)),
                currency,
                growthRate: Number(growthRate),
                startAge: cashflow.isRetirementExpense && !isOneTime ? 0 : Number(startAge),
                endAge: isOneTime ? Number(startAge) : Number(endAge),
                isOneTime: isOneTime || undefined,
                isRetirementExpense: cashflow.isRetirementExpense || undefined,
                endsAtRetirement: endsAtRetirement || undefined,
            });
            onSaved();
        } catch (err) {
            setFormError(err instanceof Error ? err.message : 'Failed to save');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className={styles.overlay} onClick={onClose}>
            <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
                <div className={styles.modalHeader}>
                    <h2>Edit {cashflow.type === 'INCOME' ? 'Income' : 'Expense'}</h2>
                    <button className={styles.closeButton} onClick={onClose}>
                        &times;
                    </button>
                </div>
                <form className={styles.form} onSubmit={handleSubmit}>
                    <div className={styles.field}>
                        <label className={styles.label}>Label (optional)</label>
                        <input
                            type="text"
                            className={styles.input}
                            placeholder={
                                cashflow.type === 'INCOME'
                                    ? 'e.g., Tech job, Freelance consulting'
                                    : "e.g., Son's college, Annual vacation"
                            }
                            value={label}
                            onChange={(e) => setLabel(e.target.value)}
                        />
                    </div>

                    <div className={styles.field}>
                        <label className={styles.label}>Category</label>
                        <select
                            className={styles.input}
                            value={category}
                            onChange={(e) => setCategory(e.target.value as CashFlowCategory)}
                        >
                            {categories.map((c) => (
                                <option key={c} value={c}>
                                    {CASHFLOW_CATEGORY_LABELS[c]}
                                </option>
                            ))}
                        </select>
                        {category === 'ASSET_SALE' && (
                            <p className={styles.ltcgNote}>
                                Treated as long-term capital gains — India 12.5%, US 0–20% based on your income that
                                year, Canada 50% inclusion at marginal rate. Enter the full proceeds; the simulation
                                applies LTCG rates automatically.
                            </p>
                        )}
                    </div>

                    <div className={styles.field}>
                        <label className={styles.label}>Country</label>
                        <select
                            className={styles.input}
                            value={country}
                            onChange={(e) => setCountry(e.target.value as Country)}
                        >
                            {COUNTRIES.map((c) => (
                                <option key={c.value} value={c.value}>
                                    {c.label}
                                </option>
                            ))}
                        </select>
                        <div className={styles.currencyIndicator}>
                            Enter amount in <strong>{currency}</strong> (local currency)
                        </div>
                    </div>

                    {isOneTime ? (
                        <div className={styles.field}>
                            <label className={styles.label}>Total Amount</label>
                            <input
                                type="text"
                                inputMode="numeric"
                                className={styles.input}
                                value={formatCurrencyInput(amount, currency)}
                                onChange={(e) => setAmount(extractNumericInput(e.target.value))}
                                required
                            />
                            {numericAmount > 0 && currency !== baseCurrency && (
                                <div className={styles.conversionNote}>
                                    ≈ {formatAmount(Math.round(convertedAmount), baseCurrency)} {baseCurrency}
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className={styles.row}>
                            <div className={styles.field}>
                                <label className={styles.label}>Monthly Amount</label>
                                <input
                                    type="text"
                                    inputMode="numeric"
                                    className={styles.input}
                                    value={formatCurrencyInput(amount, currency)}
                                    onChange={(e) => setAmount(extractNumericInput(e.target.value))}
                                    required
                                />
                                {numericAmount > 0 && currency !== baseCurrency && (
                                    <div className={styles.conversionNote}>
                                        ≈ {formatAmount(Math.round(convertedAmount), baseCurrency)} {baseCurrency}
                                    </div>
                                )}
                            </div>
                            <div className={styles.field}>
                                <label className={styles.label}>
                                    {cashflow.type === 'INCOME' ? 'Annual Growth %' : 'Annual Increase %'}
                                </label>
                                <input
                                    type="number"
                                    className={styles.input}
                                    value={growthRate}
                                    onChange={(e) => setGrowthRate(e.target.value)}
                                    step={0.5}
                                    min={0}
                                    max={20}
                                />
                                <span className={styles.fieldHint}>
                                    {cashflow.type === 'INCOME'
                                        ? 'Expected yearly raise or investment growth rate'
                                        : 'Expected yearly cost increase — approx. your local inflation rate'}
                                </span>
                            </div>
                        </div>
                    )}

                    {!cashflow.isRetirementExpense && !isOneTime && (
                        <div className={styles.field}>
                            <label className={styles.checkboxLabel}>
                                <input
                                    type="checkbox"
                                    checked={endsAtRetirement}
                                    onChange={(e) => setEndsAtRetirement(e.target.checked)}
                                />
                                Ends when I retire
                            </label>
                        </div>
                    )}

                    {isOneTime && cashflow.isRetirementExpense ? (
                        <div className={styles.atRetirementNote}>
                            No fixed age needed — this will be deducted at whatever retirement age you test on the
                            simulator slider.
                        </div>
                    ) : cashflow.isRetirementExpense && !isOneTime ? (
                        <div className={styles.row}>
                            <div className={styles.field}>
                                <p className={styles.retirementStartNote}>Starts at retirement age</p>
                            </div>
                            <div className={styles.field}>
                                <label className={styles.label}>End Age</label>
                                <input
                                    type="number"
                                    className={styles.input}
                                    value={endAge}
                                    onChange={(e) => setEndAge(e.target.value)}
                                    min={18}
                                    max={100}
                                    required
                                />
                            </div>
                        </div>
                    ) : (
                        <div className={styles.row}>
                            <div className={styles.field}>
                                <label className={styles.label}>{isOneTime ? 'Event Age' : 'Start Age'}</label>
                                <input
                                    type="number"
                                    className={styles.input}
                                    value={startAge}
                                    onChange={(e) => setStartAge(e.target.value)}
                                    min={18}
                                    max={90}
                                    required
                                />
                            </div>
                            {!isOneTime && !endsAtRetirement && (
                                <div className={styles.field}>
                                    <label className={styles.label}>End Age</label>
                                    <input
                                        type="number"
                                        className={styles.input}
                                        value={endAge}
                                        onChange={(e) => setEndAge(e.target.value)}
                                        min={Number(startAge)}
                                        max={100}
                                        required
                                    />
                                </div>
                            )}
                        </div>
                    )}

                    {formError && <p className={styles.formError}>{formError}</p>}

                    <div className={styles.actions}>
                        <button type="button" className={styles.cancelButton} onClick={onClose}>
                            Cancel
                        </button>
                        <button type="submit" className={styles.submitButton} disabled={saving}>
                            {saving ? 'Saving...' : 'Save Changes'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

interface AddCashflowModalProps {
    type: CashFlowType;
    onClose: () => void;
    onAdded: () => void;
    addCashflow: (cf: Omit<DynamicCashFlow, 'cashFlowId' | 'memberSub' | 'updatedAt'>) => Promise<DynamicCashFlow>;
    targetRetirementAge?: number;
}

function AddCashflowModal({ type, onClose, onAdded, addCashflow, targetRetirementAge }: AddCashflowModalProps) {
    const { rates } = useExchangeRates();
    const { profile } = useProfile();
    const { countryOptions, countries, retirementCountry } = useResidencyCountries();
    const baseCurrency = profile?.baseCurrency ?? 'USD';
    const categories = type === 'INCOME' ? INCOME_CATEGORIES : EXPENSE_CATEGORIES;
    const [category, setCategory] = useState<CashFlowCategory>(categories[0]);
    const [label, setLabel] = useState('');
    const [country, setCountry] = useState<Country>(countries[0] ?? 'US');

    useEffect(() => {
        if (targetRetirementAge && retirementCountry) {
            setCountry(retirementCountry);
        } else if (!targetRetirementAge && countries[0]) {
            setCountry(countries[0]);
        }
    }, [retirementCountry, countries, targetRetirementAge]);
    const currency = COUNTRY_CURRENCIES[country];
    const [amount, setAmount] = useState('');
    const [growthRate, setGrowthRate] = useState('3');
    const [startAge, setStartAge] = useState(targetRetirementAge?.toString() ?? '35');
    const [endAge, setEndAge] = useState(targetRetirementAge ? (targetRetirementAge + 30).toString() : '65');
    const [isOneTime, setIsOneTime] = useState(false);
    const [endsAtRetirement, setEndsAtRetirement] = useState(type === 'EXPENSE' && !targetRetirementAge);
    const [saving, setSaving] = useState(false);
    const [formError, setFormError] = useState<string | null>(null);

    const numericAmount = Number(extractNumericInput(amount));
    const convertedAmount =
        numericAmount > 0 && currency !== baseCurrency
            ? convertCurrency(numericAmount, currency, baseCurrency, rates)
            : numericAmount;

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setSaving(true);
        setFormError(null);
        try {
            await addCashflow({
                type,
                category,
                label: label || undefined,
                country,
                amount: Number(amount),
                currency,
                growthRate: Number(growthRate),
                startAge: targetRetirementAge !== undefined && !isOneTime ? 0 : Number(startAge),
                endAge: isOneTime ? Number(startAge) : endsAtRetirement ? 95 : Number(endAge),
                isOneTime: isOneTime || undefined,
                isRetirementExpense: targetRetirementAge !== undefined || undefined,
                endsAtRetirement: endsAtRetirement || undefined,
            });
            onAdded();
        } catch (err) {
            setFormError(err instanceof Error ? err.message : 'Failed to save');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className={styles.overlay} onClick={onClose}>
            <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
                <div className={styles.modalHeader}>
                    <h2>
                        Add{' '}
                        {type === 'INCOME'
                            ? 'Income'
                            : targetRetirementAge !== undefined
                              ? 'Retirement Expense'
                              : 'Expense'}
                    </h2>
                    <button className={styles.closeButton} onClick={onClose}>
                        &times;
                    </button>
                </div>
                <form className={styles.form} onSubmit={handleSubmit}>
                    <div className={styles.field}>
                        <label className={styles.label}>Label (optional)</label>
                        <input
                            type="text"
                            className={styles.input}
                            placeholder={
                                type === 'INCOME'
                                    ? 'e.g., Tech job, Freelance consulting'
                                    : "e.g., Son's college, Annual vacation"
                            }
                            value={label}
                            onChange={(e) => setLabel(e.target.value)}
                        />
                    </div>

                    <div className={styles.field}>
                        <label className={styles.label}>Category</label>
                        <select
                            className={styles.input}
                            value={category}
                            onChange={(e) => setCategory(e.target.value as CashFlowCategory)}
                        >
                            {categories.map((c) => (
                                <option key={c} value={c}>
                                    {CASHFLOW_CATEGORY_LABELS[c]}
                                </option>
                            ))}
                        </select>
                        {category === 'ASSET_SALE' && (
                            <p className={styles.ltcgNote}>
                                Treated as long-term capital gains — India 12.5%, US 0–20% based on your income that
                                year, Canada 50% inclusion at marginal rate. Enter the full proceeds; the simulation
                                applies LTCG rates automatically.
                            </p>
                        )}
                    </div>

                    <div className={styles.field}>
                        <label className={styles.label}>Country</label>
                        <select
                            className={styles.input}
                            value={country}
                            onChange={(e) => setCountry(e.target.value as Country)}
                        >
                            {countryOptions.map((c) => (
                                <option key={c.value} value={c.value}>
                                    {c.label}
                                </option>
                            ))}
                        </select>
                        <div className={styles.currencyIndicator}>
                            Enter amount in <strong>{currency}</strong> (local currency)
                        </div>
                    </div>

                    {(type === 'EXPENSE' || type === 'INCOME') && (
                        <div className={styles.field}>
                            <label className={styles.checkboxLabel}>
                                <input
                                    type="checkbox"
                                    checked={isOneTime}
                                    onChange={(e) => setIsOneTime(e.target.checked)}
                                />
                                {type === 'INCOME'
                                    ? 'One-time proceeds (e.g., property sale, inheritance)'
                                    : 'One-time event (e.g., home purchase, wedding)'}
                            </label>
                            {isOneTime && targetRetirementAge !== undefined && type === 'EXPENSE' && (
                                <p className={styles.warnNote}>
                                    If you've also added this as a Goal, remove one — both are deducted from your
                                    portfolio independently in the simulation.
                                </p>
                            )}
                        </div>
                    )}

                    {!targetRetirementAge && !isOneTime && (
                        <div className={styles.field}>
                            <label className={styles.checkboxLabel}>
                                <input
                                    type="checkbox"
                                    checked={endsAtRetirement}
                                    onChange={(e) => setEndsAtRetirement(e.target.checked)}
                                />
                                Ends when I retire
                            </label>
                        </div>
                    )}

                    {isOneTime ? (
                        <div className={styles.field}>
                            <label className={styles.label}>Total Amount</label>
                            <input
                                type="text"
                                inputMode="numeric"
                                className={styles.input}
                                value={formatCurrencyInput(amount, currency)}
                                onChange={(e) => setAmount(extractNumericInput(e.target.value))}
                                required
                            />
                            {numericAmount > 0 && currency !== baseCurrency && (
                                <div className={styles.conversionNote}>
                                    ≈ {formatAmount(Math.round(convertedAmount), baseCurrency)} {baseCurrency}
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className={styles.row}>
                            <div className={styles.field}>
                                <label className={styles.label}>Monthly Amount</label>
                                <input
                                    type="text"
                                    inputMode="numeric"
                                    className={styles.input}
                                    value={formatCurrencyInput(amount, currency)}
                                    onChange={(e) => setAmount(extractNumericInput(e.target.value))}
                                    required
                                />
                                {numericAmount > 0 && currency !== baseCurrency && (
                                    <div className={styles.conversionNote}>
                                        ≈ {formatAmount(Math.round(convertedAmount), baseCurrency)} {baseCurrency}
                                    </div>
                                )}
                            </div>
                            <div className={styles.field}>
                                <label className={styles.label}>
                                    {type === 'INCOME' ? 'Annual Growth %' : 'Annual Increase %'}
                                </label>
                                <input
                                    type="number"
                                    className={styles.input}
                                    value={growthRate}
                                    onChange={(e) => setGrowthRate(e.target.value)}
                                    step={0.5}
                                    min={0}
                                    max={20}
                                />
                                <span className={styles.fieldHint}>
                                    {type === 'INCOME'
                                        ? 'Expected yearly raise or investment growth rate'
                                        : 'Expected yearly cost increase — approx. your local inflation rate'}
                                </span>
                            </div>
                        </div>
                    )}

                    {isOneTime && targetRetirementAge !== undefined ? (
                        <div className={styles.atRetirementNote}>
                            No fixed age needed — this will be deducted at whatever retirement age you test on the
                            simulator slider. Drag the slider to compare how it affects retiring at 55 vs 60 vs 65.
                        </div>
                    ) : targetRetirementAge !== undefined && !isOneTime ? (
                        <div className={styles.row}>
                            <div className={styles.field}>
                                <p className={styles.retirementStartNote}>Starts at retirement age</p>
                            </div>
                            <div className={styles.field}>
                                <label className={styles.label}>End Age</label>
                                <input
                                    type="number"
                                    className={styles.input}
                                    value={endAge}
                                    onChange={(e) => setEndAge(e.target.value)}
                                    min={18}
                                    max={100}
                                    required
                                />
                            </div>
                        </div>
                    ) : (
                        <div className={styles.row}>
                            <div className={styles.field}>
                                <label className={styles.label}>{isOneTime ? 'Event Age' : 'Start Age'}</label>
                                <input
                                    type="number"
                                    className={styles.input}
                                    value={startAge}
                                    onChange={(e) => setStartAge(e.target.value)}
                                    min={18}
                                    max={90}
                                    required
                                />
                            </div>
                            {!isOneTime && !endsAtRetirement && (
                                <div className={styles.field}>
                                    <label className={styles.label}>End Age</label>
                                    <input
                                        type="number"
                                        className={styles.input}
                                        value={endAge}
                                        onChange={(e) => setEndAge(e.target.value)}
                                        min={Number(startAge)}
                                        max={100}
                                        required
                                    />
                                </div>
                            )}
                        </div>
                    )}

                    {formError && <p className={styles.formError}>{formError}</p>}

                    <div className={styles.actions}>
                        <button type="button" className={styles.cancelButton} onClick={onClose}>
                            Cancel
                        </button>
                        <button type="submit" className={styles.submitButton} disabled={saving}>
                            {saving ? 'Saving...' : 'Add'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
